import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ripple = keyframes`
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
  animation: fadeIn 0.8s ease;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const VisionTitle = styled.h2`
  font-size: 2.5rem;
  color: #2e7d32;
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  display: inline-block;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #4CAF50, #ff9800);
    border-radius: 2px;
    animation: expandWidth 1s ease;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const VisionText = styled.p`
  text-align: center;
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const ImageShowcase = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const ShowcaseItem = styled.div`
  position: relative;
  height: 250px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  transition: transform 0.4s;
  animation: ${fadeIn} 0.6s ease forwards;
  opacity: 0;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 15px 40px rgba(0,0,0,0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%);
    z-index: 1;
  }
`;

const ShowcaseIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 5rem;
  z-index: 2;
  opacity: 0.9;
`;

const ShowcaseLabel = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 0;
  right: 0;
  text-align: center;
  color: white;
  font-weight: bold;
  z-index: 2;
  font-size: 1.1rem;
`;

const ImpactStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #4CAF50 0%, #2e7d32 100%);
  color: white;
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  transition: transform 0.3s;
  animation: ${fadeInUp} 0.6s ease forwards;
  opacity: 0;
  position: relative;
  overflow: hidden;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: ${ripple} 3s ease-in-out infinite;
  }

  &:hover {
    transform: translateY(-10px) scale(1.05);
  }
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 1;
`;

const About: React.FC = () => {
  return (
    <Section id="about">
      <VisionTitle>Our Vision</VisionTitle>
      <VisionText>
        A transformed Africa where communities enjoy optimal health, meaningful education, and productive agriculture—each reinforcing the others for sustainable development.
      </VisionText>

      <ImageShowcase>
        <ShowcaseItem style={{ background: 'linear-gradient(135deg, #4CAF50 0%, #2e7d32 100%)' }}>
          <ShowcaseIcon>👨‍👩‍👧‍👦</ShowcaseIcon>
          <ShowcaseLabel>Community Empowerment</ShowcaseLabel>
        </ShowcaseItem>
        <ShowcaseItem style={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}>
          <ShowcaseIcon>🌍</ShowcaseIcon>
          <ShowcaseLabel>Pan-African Impact</ShowcaseLabel>
        </ShowcaseItem>
        <ShowcaseItem style={{ background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)' }}>
          <ShowcaseIcon>💡</ShowcaseIcon>
          <ShowcaseLabel>Innovation & Research</ShowcaseLabel>
        </ShowcaseItem>
        <ShowcaseItem style={{ background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)' }}>
          <ShowcaseIcon>🤝</ShowcaseIcon>
          <ShowcaseLabel>Strategic Partnerships</ShowcaseLabel>
        </ShowcaseItem>
      </ImageShowcase>

      <ImpactStats>
        <StatCard>
          <StatNumber>464M</StatNumber>
          <p>People in extreme poverty in Sub-Saharan Africa</p>
        </StatCard>
        <StatCard>
          <StatNumber>3</StatNumber>
          <p>Interconnected sectors we address simultaneously</p>
        </StatCard>
        <StatCard>
          <StatNumber>740M</StatNumber>
          <p>Additional workforce by 2050—our opportunity window</p>
        </StatCard>
      </ImpactStats>
    </Section>
  );
};

export default About;